import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { complianceAPI } from '../lib/api';
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronRight,
  Calendar,
} from 'lucide-react';

export const Compliance = () => {
  const { t } = useTranslation();
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await complianceAPI.getStatus();
        setCompliance(response.data);
      } catch (error) {
        console.error('Failed to fetch compliance:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10', label: t('courses.completed', 'Completed') };
      case 'expired':
        return { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10', label: t('compliance.expired', 'Expired') };
      case 'in_progress':
        return { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-500/10', label: t('courses.inProgress', 'In Progress') };
      default:
        return { icon: ShieldCheck, color: 'text-muted-foreground', bg: 'bg-muted', label: t('courses.notStarted', 'Not Started') };
    }
  };

  const completedCount = compliance.filter(c => c.status === 'completed').length;
  const expiredCount = compliance.filter(c => c.status === 'expired').length;
  const pendingCount = compliance.filter(c => c.status !== 'completed' && c.status !== 'expired').length;
  const totalCount = compliance.length;
  const completionRate = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="compliance-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-green-500" />
            {t('compliance.title', 'Compliance Training')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('compliance.subtitle', 'Stay compliant with required training certifications')}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4" data-testid="stat-completion-rate">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-green-500/10">
                <ShieldCheck className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('compliance.completionRate', 'Completion Rate')}</p>
                <p className="text-2xl font-bold">{Math.round(completionRate)}%</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-completed">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-green-500/10">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('courses.completed', 'Completed')}</p>
                <p className="text-2xl font-bold text-green-500">{completedCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-pending">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-blue-500/10">
                <Clock className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('compliance.pending', 'Pending')}</p>
                <p className="text-2xl font-bold text-blue-500">{pendingCount}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-expired">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-red-500/10">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('compliance.expired', 'Expired')}</p>
                <p className="text-2xl font-bold text-red-500">{expiredCount}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">{t('compliance.overallCompliance', 'Overall Compliance')}</span>
              <span className="text-sm text-muted-foreground">
                {completedCount} {t('common.of', 'of')} {totalCount} {t('compliance.complete', 'complete')}
              </span>
            </div>
            <Progress value={completionRate} className="h-3" />
          </CardContent>
        </Card>

        {/* Compliance List */}
        <Card data-testid="compliance-list">
          <CardHeader>
            <CardTitle>{t('compliance.requiredTraining', 'Required Training')}</CardTitle>
          </CardHeader>
          <CardContent>
            {compliance.length > 0 ? (
              <div className="space-y-4">
                {compliance.map((item) => {
                  const statusInfo = getStatusInfo(item.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <div
                      key={item.course_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                        item.status === 'expired' ? 'border-red-500/50 bg-red-500/5' : ''
                      }`}
                      data-testid={`compliance-item-${item.course_id}`}
                    >
                      <div className={`p-3 rounded-full ${statusInfo.bg}`}>
                        <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold">{item.course_title}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm">
                          <Badge variant={
                            item.status === 'completed' ? 'default' :
                            item.status === 'expired' ? 'destructive' :
                            'secondary'
                          }>
                            {statusInfo.label}
                          </Badge>
                          {item.due_date && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="w-4 h-4" />
                              Due: {new Date(item.due_date).toLocaleDateString()}
                            </span>
                          )}
                          {item.completed_at && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <CheckCircle2 className="w-4 h-4" />
                              Completed: {new Date(item.completed_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        asChild
                        variant={item.status === 'expired' ? 'destructive' : 'outline'}
                        size="sm"
                        className="rounded-full"
                        data-testid={`compliance-action-${item.course_id}`}
                      >
                        <Link to={`/courses/${item.course_id}`}>
                          {item.status === 'completed' ? 'Review' :
                           item.status === 'expired' ? 'Renew Now' :
                           item.status === 'in_progress' ? 'Continue' : 'Start'}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShieldCheck className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Compliance Training</h3>
                <p className="text-muted-foreground">
                  No compliance training required at this time.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Compliance;
